import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { verifyAdminAccess } from '../utils/adminAuth';
import { useAdminAuth } from '../hooks/useAdminAuth';
import API_URL from '../config/api';
import GameManagement from '../components/GameManagement';
import CreatorManagement from '../components/CreatorManagement';
import GameScraper from '../components/GameScraper';
import GameAgent from '../components/GameAgent';

function DashboardShell({ children, onRefresh, refreshing, onBack, title = 'Admin Hub' }) {
  return (
    <div className="min-h-screen bg-dark-bg overflow-x-hidden">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,235,0.08)_0%,_transparent_55%)]" aria-hidden="true" />

      <header className="fixed top-2 sm:top-4 left-0 right-0 z-50 border border-dark-border rounded-2xl sm:rounded-3xl mx-2 md:mx-5 px-3 sm:px-4 md:px-8 py-2.5 sm:py-3 md:py-4 shadow-2xl bg-gradient-to-br from-dark-surface via-dark-card to-dark-border backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
            <img src="/images/logo_white.png" alt="Nightfall" className="h-6 sm:h-7 md:h-8 w-auto flex-shrink-0" />
            <span className="font-normal text-sm sm:text-xl md:text-2xl text-white tracking-tight sm:border-l-2 sm:border-white sm:pl-3 md:pl-4 truncate">
              {title}
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200 disabled:opacity-50"
                aria-label="Refresh dashboard"
              >
                <svg
                  className={`w-5 h-5 md:w-6 md:h-6 text-white ${refreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onBack}
              className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
              aria-label="Exit to store"
            >
              <svg
                className="w-5 h-5 md:w-6 md:h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="relative pt-[4.5rem] sm:pt-24 md:pt-32 px-3 sm:px-4 md:px-5 pb-8 sm:pb-12 w-full max-w-full">
        {children}
      </main>
    </div>
  );
}

function PanelCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl ${className}`}>
      {(title || subtitle) && (
        <div className="mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-dark-border flex flex-wrap items-end justify-between gap-2">
          {title && <h2 className="font-semibold text-lg sm:text-xl lg:text-2xl text-white">{title}</h2>}
          {subtitle && <span className="text-sm text-white/50">{subtitle}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function StatCard({ label, value, subtext, variant = 'secondary', className = '' }) {
  const isHero = variant === 'hero';

  return (
    <div
      className={`transition-all duration-200 ${
        isHero
          ? 'rounded-2xl sm:rounded-3xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface p-5 sm:p-6 lg:p-8 shadow-xl hover:border-white/20'
          : 'rounded-xl sm:rounded-2xl border border-dark-border/90 bg-gradient-to-br from-dark-card/90 to-dark-surface/80 p-3.5 sm:p-4 lg:p-5 shadow-md hover:border-white/15'
      } ${className}`}
    >
      <p
        className={`font-medium uppercase tracking-wider text-white/45 ${
          isHero ? 'text-xs mb-3' : 'text-[11px] mb-1.5'
        }`}
      >
        {label}
      </p>
      <p
        className={`font-semibold text-white tracking-tight tabular-nums ${
          isHero ? 'text-3xl sm:text-4xl lg:text-5xl' : 'text-lg sm:text-xl lg:text-2xl text-white/90'
        }`}
      >
        {value}
      </p>
      {subtext && (
        <p className={`mt-2 ${isHero ? 'text-sm text-white/50' : 'text-xs text-white/40'}`}>{subtext}</p>
      )}
    </div>
  );
}

const ADMIN_NAV = [
  { id: 'games', label: 'Games' },
  { id: 'creators', label: 'Creators' },
  { id: 'scraper', label: 'Scraper' },
  { id: 'agent', label: 'Agent' },
  { id: 'downloads', label: 'Downloads' },
];

const ADMIN_PAGE_TITLES = {
  games: 'Game Management',
  creators: 'Creator Management',
  scraper: 'Game Scraper',
  agent: 'Agent',
  downloads: 'Download Statistics',
};

function SidebarMiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-dark-border/80 bg-dark-bg/40 px-3 py-2.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-1">{label}</p>
      <p className="text-base font-semibold text-white tabular-nums leading-none">{value}</p>
    </div>
  );
}

function SidebarHighlight({ label, value, subtext }) {
  return (
    <div className="rounded-xl sm:rounded-2xl border border-dark-border bg-gradient-to-br from-dark-bg/50 to-dark-surface/30 px-4 py-3.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-1.5">{label}</p>
      <p className="text-2xl font-semibold text-white tabular-nums tracking-tight">{value}</p>
      {subtext && <p className="text-xs text-white/45 mt-1.5">{subtext}</p>}
    </div>
  );
}

function SidebarSection({ title, subtitle, children }) {
  return (
    <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-4 shadow-xl">
      <div className="mb-3 pb-3 border-b border-dark-border/80">
        <h3 className="font-semibold text-sm text-white">{title}</h3>
        {subtitle && <p className="text-[11px] text-white/45 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function AdminSidebar({ stats, statsLoading, username }) {
  return (
    <aside className="w-full lg:w-[22%] lg:min-w-[260px] lg:max-w-[300px] flex-shrink-0 flex flex-col gap-3 sm:gap-4 order-2 lg:order-1 lg:sticky lg:top-28">
      <div className="px-1">
        <p className="text-[11px] font-medium uppercase tracking-wider text-white/35">Overview</p>
        {username && (
          <p className="text-sm text-white/55 mt-1 truncate">Signed in as @{username}</p>
        )}
      </div>

      {statsLoading && !stats ? (
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-8 flex justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-accent-blue/30 border-t-accent-blue animate-spin" />
        </div>
      ) : stats ? (
        <>
          <SidebarSection title="Users" subtitle="Account activity">
            <div className="space-y-3">
              <SidebarHighlight
                label="Total users"
                value={stats.users.total.toLocaleString()}
              />
              <div className="grid grid-cols-2 gap-2">
                <SidebarMiniStat label="New today" value={stats.users.newToday.toLocaleString()} />
                <SidebarMiniStat label="Active today" value={stats.users.activeToday.toLocaleString()} />
              </div>
            </div>
          </SidebarSection>

          {stats.visits && (
            <SidebarSection title="Traffic" subtitle="Site visits">
              <div className="space-y-3">
                <SidebarHighlight
                  label="Visits today"
                  value={stats.visits.today?.toLocaleString() || '0'}
                  subtext={`${stats.visits.uniqueVisitorsToday || 0} unique visitors`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <SidebarMiniStat label="This week" value={stats.visits.week?.toLocaleString() || '0'} />
                  <SidebarMiniStat label="This month" value={stats.visits.month.toLocaleString()} />
                  <SidebarMiniStat label="Total visits" value={stats.visits.total.toLocaleString()} />
                  <SidebarMiniStat label="Unique week" value={stats.visits.uniqueVisitorsWeek.toLocaleString()} />
                </div>
              </div>
            </SidebarSection>
          )}

          <SidebarSection title="Downloads" subtitle="File activity">
            <div className="space-y-3">
              <SidebarHighlight
                label="Downloads today"
                value={stats.downloads.today.toLocaleString()}
                subtext={`${stats.downloads.todayStats.uniqueUsers} users · ${stats.downloads.todayStats.uniqueGames} games`}
              />
              <div className="grid grid-cols-2 gap-2">
                <SidebarMiniStat label="This week" value={stats.downloads.week.toLocaleString()} />
                <SidebarMiniStat label="All time" value={stats.downloads.total.toLocaleString()} />
              </div>
            </div>
          </SidebarSection>
        </>
      ) : (
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-5">
          <p className="text-sm text-white/45 text-center">Metrics unavailable</p>
        </div>
      )}
    </aside>
  );
}

function AdminNavTabs({ activeTab, onTabChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {ADMIN_NAV.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
            activeTab === tab.id
              ? 'bg-accent-blue/20 border-accent-blue/30 text-accent-blue'
              : 'bg-dark-card/60 border-dark-border text-white/60 hover:text-white hover:border-white/20'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function AdminDownloadsPage({ stats, formatDate }) {
  if (!stats) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          variant="hero"
          label="Downloads Today"
          value={stats.downloads.today.toLocaleString()}
          subtext={`${stats.downloads.todayStats.uniqueUsers} users · ${stats.downloads.todayStats.uniqueGames} games`}
        />
        <StatCard
          label="Total Downloads"
          value={stats.downloads.total.toLocaleString()}
          subtext={`${stats.downloads.week} this week · ${stats.downloads.month} this month`}
        />
        <StatCard
          label="Bandwidth Today"
          value={formatBytes(stats.downloads.todayStats.bandwidth)}
        />
        <StatCard
          label="Unique Games"
          value={stats.downloads.todayStats.uniqueGames.toLocaleString()}
        />
      </div>

      <PanelCard title="Download Statistics" subtitle="Performance metrics">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">Platform Distribution</h3>
            <span className="text-white/40 text-xs uppercase tracking-wider">Last 7 days</span>
          </div>
          <div className="space-y-3">
            {stats.platforms.map((platform, idx) => (
              <PlatformBar key={idx} platform={platform} total={stats.platforms.reduce((sum, p) => sum + p.count, 0)} />
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">File Type Distribution</h3>
            <span className="text-white/40 text-xs uppercase tracking-wider">Last 7 days</span>
          </div>
          <div className="space-y-3">
            {stats.fileTypes.map((fileType, idx) => (
              <FileTypeBar key={idx} fileType={fileType} total={stats.fileTypes.reduce((sum, f) => sum + f.count, 0)} />
            ))}
          </div>
        </div>
      </PanelCard>

      <PanelCard title="Most Downloaded Games" subtitle="Last 7 days">
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Game</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Downloads</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Users</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Bandwidth</th>
                <th className="text-center py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Platforms</th>
              </tr>
            </thead>
            <tbody>
              {stats.games.map((game, idx) => (
                <tr key={idx} className="border-b border-dark-border/50 hover:bg-dark-bg/30 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {game.game_cover && (
                        <img src={game.game_cover} alt={game.game_name} className="w-12 h-12 rounded-xl object-cover border border-dark-border" />
                      )}
                      <div>
                        <div className="text-white font-medium">{game.game_name}</div>
                        <div className="text-white/40 text-sm">ID: {game.game_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{parseInt(game.download_count).toLocaleString()}</td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{parseInt(game.unique_users).toLocaleString()}</td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{formatBytes(parseInt(game.total_bandwidth))}</td>
                  <td className="text-center py-3 px-4">
                    <div className="flex gap-2 justify-center flex-wrap">
                      {parseInt(game.windows_count) > 0 && <span className="px-2 py-1 bg-dark-bg/50 border border-dark-border text-white/70 rounded-lg text-xs font-mono">WIN: {game.windows_count}</span>}
                      {parseInt(game.mac_count) > 0 && <span className="px-2 py-1 bg-dark-bg/50 border border-dark-border text-white/70 rounded-lg text-xs font-mono">MAC: {game.mac_count}</span>}
                      {parseInt(game.android_count) > 0 && <span className="px-2 py-1 bg-dark-bg/50 border border-dark-border text-white/70 rounded-lg text-xs font-mono">AND: {game.android_count}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelCard>

      <PanelCard title="Recent Downloads" subtitle="Last 50 events">
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Time</th>
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Game</th>
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">User</th>
                <th className="text-center py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Platform</th>
                <th className="text-center py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Type</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Size</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentDownloads.slice(0, 20).map((download, idx) => (
                <tr key={idx} className="border-b border-dark-border/50 hover:bg-dark-bg/30 transition-colors">
                  <td className="py-3 px-4 text-white/60 text-sm">{formatDate(download.createdAt)}</td>
                  <td className="py-3 px-4 text-white">{download.gameName}</td>
                  <td className="py-3 px-4">
                    {download.user ? (
                      <div>
                        <div className="text-white">{download.user.username}</div>
                        <div className="text-white/40 text-xs">{download.user.email}</div>
                      </div>
                    ) : (
                      <span className="text-white/40">Anonymous</span>
                    )}
                  </td>
                  <td className="text-center py-3 px-4">
                    <span className="px-2 py-1 bg-dark-bg/50 border border-dark-border text-white/70 rounded-lg text-xs uppercase font-mono">{download.platform}</span>
                  </td>
                  <td className="text-center py-3 px-4">
                    <span className="px-2 py-1 bg-dark-bg/50 border border-dark-border text-white/70 rounded-lg text-xs uppercase font-mono">{download.type}</span>
                  </td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{formatBytes(download.fileSize)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelCard>

      <PanelCard title="Top Users by Downloads" subtitle="Last 7 days">
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">User</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Downloads</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Games</th>
                <th className="text-right py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Bandwidth</th>
                <th className="text-left py-3 px-4 text-white/50 text-xs uppercase tracking-wider font-medium">Last Login</th>
              </tr>
            </thead>
            <tbody>
              {stats.topUsers.map((topUser, idx) => (
                <tr key={idx} className="border-b border-dark-border/50 hover:bg-dark-bg/30 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <div className="text-white font-medium">{topUser.username}</div>
                      <div className="text-white/40 text-sm">{topUser.email}</div>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{topUser.downloadCount.toLocaleString()}</td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{topUser.gamesDownloaded}</td>
                  <td className="text-right py-3 px-4 text-white tabular-nums">{formatBytes(topUser.totalBandwidth)}</td>
                  <td className="py-3 px-4 text-white/60 text-sm">{formatDate(topUser.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelCard>
    </div>
  );
}

function AdminDashboard() {
  useSEO({
    title: 'Admin Dashboard | Nightfall',
    description: 'Admin dashboard for Nightfall',
    url: 'https://nightfallshop.com/admin/dashboard'
  });
  
  const navigate = useNavigate();
  const {
    user,
    refreshUser,
    hasAdminAccess,
    hasLegacyAdmin,
    isRoleAdmin,
    getAuthHeader,
    removeLegacyAdminToken,
    loading: authLoading,
  } = useAdminAuth();
  const [isVerifying, setIsVerifying] = useState(true);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('games');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(window.location.search);
    if (tabId === 'games') {
      params.delete('tab');
    } else {
      params.set('tab', tabId);
    }
    const query = params.toString();
    navigate(query ? `/admin/dashboard?${query}` : '/admin/dashboard', { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ADMIN_NAV.some((item) => item.id === tab)) {
      setActiveTab(tab);
    }
  }, []);

  const fetchStats = useCallback(async (authHeaderOverride) => {
    try {
      setLoading(true);
      setError(null);
      const authHeader = authHeaderOverride || getAuthHeader();

      if (!authHeader) {
        throw new Error('Not authorized');
      }

      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const verifyAuth = async () => {
      if (authLoading) {
        return;
      }

      if (!hasAdminAccess) {
        navigate('/', { replace: true });
        return;
      }

      let authHeader = getAuthHeader();
      let verification = await verifyAdminAccess(authHeader);

      if (!verification.valid && isRoleAdmin) {
        await refreshUser();
        authHeader = getAuthHeader();
        verification = await verifyAdminAccess(authHeader);
      }

      if (!verification.valid) {
        if (hasLegacyAdmin) {
          removeLegacyAdminToken();
          navigate('/admin', { replace: true });
          return;
        }
        if (isRoleAdmin) {
          setError('Admin access denied. Your account may not have admin privileges.');
          setIsVerifying(false);
          return;
        }
        navigate('/', { replace: true });
        return;
      }

      setIsVerifying(false);
      fetchStats(authHeader);
    };

    verifyAuth();
    const verifyInterval = setInterval(verifyAuth, 5 * 60 * 1000);
    return () => clearInterval(verifyInterval);
  }, [
    authLoading,
    hasAdminAccess,
    hasLegacyAdmin,
    isRoleAdmin,
    getAuthHeader,
    refreshUser,
    navigate,
    fetchStats,
    removeLegacyAdminToken,
  ]);

  const goToStore = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isVerifying) {
    return (
      <DashboardShell onBack={goToStore}>
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-12 h-12 rounded-full border-2 border-accent-blue/30 border-t-accent-blue animate-spin mb-4" />
          <p className="text-white/60 font-medium">Verifying admin access...</p>
        </div>
      </DashboardShell>
    );
  }

  const renderMainContent = () => {
    if (activeTab === 'downloads') {
      if (loading && !stats) {
        return (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 rounded-full border-2 border-accent-blue/30 border-t-accent-blue animate-spin mb-4" />
            <p className="text-white/60 font-medium">Loading download statistics...</p>
          </div>
        );
      }
      return <AdminDownloadsPage stats={stats} formatDate={formatDate} />;
    }

    if (activeTab === 'games') {
      return <GameManagement />;
    }
    if (activeTab === 'creators') {
      return <CreatorManagement />;
    }
    if (activeTab === 'scraper') {
      return <GameScraper />;
    }
    if (activeTab === 'agent') {
      return <GameAgent />;
    }

    return null;
  };

  return (
    <DashboardShell
      onBack={goToStore}
      onRefresh={() => fetchStats()}
      refreshing={loading}
    >
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 items-start w-full">
        <div className="w-full lg:w-[78%] lg:flex-1 min-w-0 flex flex-col gap-4 sm:gap-6 lg:gap-8 order-1 lg:order-2">
          <div className="flex flex-col gap-4 sm:gap-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-white/40 mb-1 sm:mb-2">Admin</p>
              <h1 className="font-semibold text-2xl sm:text-3xl lg:text-4xl text-white">
                {ADMIN_PAGE_TITLES[activeTab] || 'Admin Hub'}
              </h1>
            </div>

            <AdminNavTabs activeTab={activeTab} onTabChange={handleTabChange} />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-400 text-sm">
              {error}
            </div>
          )}

          {renderMainContent()}
        </div>

        <AdminSidebar
          stats={stats}
          statsLoading={loading}
          username={user?.username}
        />
      </div>
    </DashboardShell>
  );
}

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

function PlatformBar({ platform, total }) {
  const percentage = total > 0 ? (platform.count / total) * 100 : 0;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-white uppercase text-sm font-medium tracking-wider">{platform.platform}</span>
        <span className="text-white/50 text-sm font-mono">{platform.count.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-dark-bg/50 rounded h-1.5 mb-1">
        <div
          className="h-1.5 rounded bg-white/20"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-white/30 text-xs font-mono">
        {platform.uniqueUsers} users | {formatBytes(platform.bandwidth)}
      </div>
    </div>
  );
}

function FileTypeBar({ fileType, total }) {
  const percentage = total > 0 ? (fileType.count / total) * 100 : 0;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-white uppercase text-sm font-medium tracking-wider">{fileType.type}</span>
        <span className="text-white/50 text-sm font-mono">{fileType.count.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-dark-bg/50 rounded h-1.5 mb-1">
        <div
          className="h-1.5 rounded bg-white/20"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-white/30 text-xs font-mono">
        {fileType.uniqueUsers} users | {formatBytes(fileType.bandwidth)}
      </div>
    </div>
  );
}

export default AdminDashboard;
